import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  FolderIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArchiveBoxIcon,
  DocumentDuplicateIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { useDocumentAPI } from '../../../../../dms/DocumentAPI';

interface DocumentDashboardMetrics {
  totalDocuments: number;
  activeDocuments: number;
  archivedDocuments: number;
  recentUploads: number;
  documentsByType: Record<string, number>;
  documentsByPracticeArea: Record<string, number>;
  storageUsage: {
    totalStorage: number;
    usedStorage: number;
    availableStorage: number;
    percentage: number;
  };
  recentActivity: Array<{
    id: string;
    type: 'upload' | 'edit' | 'archive' | 'delete';
    documentName: string;
    userName: string;
    timestamp: string;
  }>;
  documentStageFlow: {
    stageDistribution: Record<string, number>;
    averageProcessingTime: number;
    bottlenecks: string[];
  };
}

interface DocumentDashboardProps {
  className?: string;
}

const DocumentDashboard: React.FC<DocumentDashboardProps> = ({ className = '' }) => {
  const [metrics, setMetrics] = useState<DocumentDashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'3M' | '6M' | '1Y'>('6M');
  
  // Use the DocumentAPI hook
  const documentAPI = useDocumentAPI();

  useEffect(() => {
    loadDashboardData();
  }, [selectedTimeframe]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get firm-wide document analytics from DMS API
      console.log('📊 Requesting DMS analytics dashboard summary...');
      const dashboardResponse = await documentAPI.getAnalyticsDashboardSummary({
        date_range: selectedTimeframe === '3M' ? '90d' : selectedTimeframe === '6M' ? '180d' : '365d'
      });

      console.log('📊 DMS Analytics Response:', dashboardResponse);
      const summary = dashboardResponse.dashboard_summary;
      console.log('📊 Dashboard Summary:', summary);
      
      // Map DMS analytics data to dashboard metrics
      const realMetrics: DocumentDashboardMetrics = {
        totalDocuments: summary.overview?.total_documents || 0,
        activeDocuments: summary.overview?.total_classified || 0,
        archivedDocuments: Math.max(0, (summary.overview?.total_documents || 0) - (summary.overview?.total_classified || 0)),
        recentUploads: summary.real_time?.recent_classifications || 0,
        documentsByType: summary.distributions?.document_types || {
          'Motion': summary.distributions?.document_types?.motion || 0,
          'Unknown': summary.distributions?.document_types?.unknown || 0,
          'Response': summary.distributions?.document_types?.response_to_admission || 0,
          'Contract': 0,
          'Pleading': 0
        },
        documentsByPracticeArea: {
          'Document Processing': summary.workflow?.workflows_triggered || 0,
          'Matter Assignments': summary.workflow?.matter_assignments || 0,
          'Party Extractions': summary.workflow?.party_extractions || 0
        },
        storageUsage: {
          totalStorage: 1000, // GB - would come from storage service
          usedStorage: Math.floor((summary.overview?.total_documents || 0) * 2.5), // Estimated 2.5MB per document
          availableStorage: 1000 - Math.floor((summary.overview?.total_documents || 0) * 2.5),
          percentage: Math.floor(((summary.overview?.total_documents || 0) * 2.5 / 1000) * 100)
        },
        recentActivity: [
          {
            id: '1',
            type: 'upload' as const,
            documentName: 'Recent Classification Activity',
            userName: 'System',
            timestamp: dashboardResponse.generated_at
          }
        ],
        documentStageFlow: {
          stageDistribution: {
            'classified': summary.overview?.total_classified || 0,
            'unclassified': Math.max(0, (summary.overview?.total_documents || 0) - (summary.overview?.total_classified || 0)),
            'processing': summary.real_time?.current_processing || 0,
            'queued': summary.real_time?.queue_length || 0
          },
          averageProcessingTime: (summary.performance?.average_processing_time_ms || 0) / 1000, // Convert to seconds
          bottlenecks: summary.real_time?.alerts || []
        }
      };
      
      setMetrics(realMetrics);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load firm-wide document analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">{error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-2 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Color schemes
  const colors = {
    primary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    indigo: '#6366F1'
  };

  // Prepare visualization data
  const documentTypeData = Object.entries(metrics.documentsByType).map(([type, count]) => ({
    name: type,
    value: count as number,
    percentage: Math.round(((count as number) / metrics.totalDocuments) * 100)
  }));

  const practiceAreaData = Object.entries(metrics.documentsByPracticeArea).map(([area, count]) => ({
    name: area,
    value: count as number,
    percentage: Math.round(((count as number) / metrics.totalDocuments) * 100)
  }));

  const stageFlowData = Object.entries(metrics.documentStageFlow.stageDistribution).map(([stage, count]) => ({
    stage: stage.charAt(0).toUpperCase() + stage.slice(1),
    count: count as number,
    cumulative: count as number
  }));

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Document Dashboard</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive overview of firm document management and workflow
          </p>
        </div>
        
        {/* Timeframe selector */}
        <div className="flex rounded-md shadow-sm">
          {(['3M', '6M', '1Y'] as const).map((timeframe) => (
            <button
              key={timeframe}
              onClick={() => setSelectedTimeframe(timeframe)}
              className={`px-3 py-2 text-sm font-medium ${
                selectedTimeframe === timeframe
                  ? 'bg-lex-accent text-lex-accent-contrast'
                  : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              } ${
                timeframe === '3M' ? 'rounded-l-md' : 
                timeframe === '1Y' ? 'rounded-r-md' : ''
              } border border-gray-300 dark:border-gray-600`}
            >
              {timeframe}
            </button>
          ))}
        </div>
      </div>

      {/* Core Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Documents */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FolderIcon className="h-8 w-8 text-lex-accent" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Documents</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{metrics.totalDocuments.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Active Documents */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentDuplicateIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Documents</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{metrics.activeDocuments.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Recent Uploads */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CloudArrowUpIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Recent Uploads</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{metrics.recentUploads}</p>
            </div>
          </div>
        </div>

        {/* Archived Documents */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArchiveBoxIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Archived Documents</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{metrics.archivedDocuments.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document Types Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Documents by Type</h3>
            <FolderIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {documentTypeData.slice(0, 5).map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: Object.values(colors)[index % Object.values(colors).length] }}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}</span>
                  <span className="text-xs text-gray-500 ml-1">({item.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Storage Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Storage Usage</h3>
            <ArchiveBoxIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Used Storage</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {metrics.storageUsage.usedStorage} GB / {metrics.storageUsage.totalStorage} GB
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-lex-accent h-2 rounded-full transition-all duration-300"
                  style={{ width: `${metrics.storageUsage.percentage}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {metrics.storageUsage.percentage.toFixed(1)}% used
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Available</p>
                <p className="text-lg font-semibold text-green-600">{metrics.storageUsage.availableStorage} GB</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Used</p>
                <p className="text-lg font-semibold text-lex-accent">{metrics.storageUsage.usedStorage} GB</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Practice Area Distribution and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Documents by Practice Area */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Documents by Practice Area</h3>
            <UsersIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {practiceAreaData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: Object.values(colors)[index % Object.values(colors).length] }}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}</span>
                  <span className="text-xs text-gray-500 ml-1">({item.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
            <ClockIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {metrics.recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {activity.type === 'upload' && <CloudArrowUpIcon className="h-4 w-4 text-lex-accent" />}
                  {activity.type === 'edit' && <DocumentDuplicateIcon className="h-4 w-4 text-green-600" />}
                  {activity.type === 'archive' && <ArchiveBoxIcon className="h-4 w-4 text-yellow-600" />}
                  {activity.type === 'delete' && <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">
                    {activity.documentName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {activity.type} by {activity.userName} • {new Date(activity.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Document Stage Flow */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Document Processing Flow</h3>
          <ArrowTrendingUpIcon className="h-5 w-5 text-gray-400" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {stageFlowData.map((stage, index) => (
            <div key={stage.stage} className="text-center">
              <div className="relative">
                <div 
                  className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white font-semibold text-lg"
                  style={{ backgroundColor: Object.values(colors)[index % Object.values(colors).length] }}
                >
                  {stage.count}
                </div>
                {index < stageFlowData.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gray-300 dark:bg-gray-600 transform -translate-y-1/2" />
                )}
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{stage.stage}</p>
              <p className="text-xs text-gray-500">
                {Math.round((stage.count / metrics.totalDocuments) * 100)}%
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Average processing time: <span className="font-semibold">{metrics.documentStageFlow.averageProcessingTime} days</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentDashboard;